<template>
  <div>
    <loading-overlay v-if="loading" />
    <title-row
      :title="`Kártya: ${this.form.card_number ?? 'Betöltés...'}`"
      :subtitle="`Tulajdonos: ${this.form.owner?.name ?? 'Betöltés...'}`"
      :breadcrumb-items="[
        {
          text: 'Kártyák',
          to: { name: 'CardList' },
        },
        {
          text: this.form.card_number,
          disabled: true,
        },
      ]"
    >
      <template #buttons>
        <title-row-button
          v-if="form.status?.name === 'permInactive'"
          @click="setRFID"
          :disabled="loading"
        >
          <v-icon left>mdi-card-search</v-icon>
          Kártya beolvasása
        </title-row-button>
        <title-row-button
          v-if="form.status?.name !== 'idReading'"
          @click="save"
          :disabled="loading"
        >
          <v-icon left>mdi-check</v-icon>
          Mentés
        </title-row-button>
      </template>
    </title-row>

    <loading-overlay v-if="loading" />

    <v-card class="rounded-xl overflow-hidden" flat>
      <v-tabs v-model="tab" align-tabs="center" color="text--primary">
        <v-tabs-slider color="primary"></v-tabs-slider>
        <v-tab key="meta"> Metaadatok </v-tab>
        <v-tab key="parking"> Parkolás </v-tab>
        <v-tab key="assets"> Eszközök </v-tab>
        <v-tab key="access_log"> Belépés napló </v-tab>
        <v-tab key="system_log"> Rendszer napló </v-tab>
        <v-tab key="access_check"> Ellenőrzés </v-tab>

        <v-tabs-items v-model="tab">
          <v-tab-item key="meta" :transition="false">
            <v-row dense>
              <v-col cols="12" lg="5">
                <v-card flat>
                  <v-card-title>Kártya adatok</v-card-title>
                  <v-card-text>
                    <v-masked-text-field
                      label="Kártyaszám"
                      v-model="form.card_number"
                      filled
                      readonly
                    />

                    <v-card flat color="muted" class="px-4 mb-8 d-flex align-center">
                      <v-switch
                        label="Aktív kártya"
                        class="py-2 my-2"
                        hide-details
                        v-model="form.active"
                        inset
                        readonly
                      />
                    </v-card>

                    <v-masked-text-field
                      label="RFID"
                      v-model="form.rf_id"
                      :mask="$config.inputMasks.rfId"
                      persistent-hint
                      placeholder="Érintse a kártyát az olvasóhoz"
                      filled
                      readonly
                      :error-messages="errors.rf_id"
                      v-uppercase
                      :hint="
                        form.status && form.status.name_ && form.status.name_ !== 'printReady'
                          ? 'Nem módosítható'
                          : ''
                      "
                    />
                    <v-text-field
                      label="Státusz"
                      :value="form.status && form.status.name"
                      readonly
                      hint="Nem módosítható"
                      persistent-hint
                      filled
                    />
                    <v-autocomplete
                      v-if="hasFeature('CARD_TEMPLATE')"
                      label="Sablon"
                      v-model="form.template_id"
                      :items="cardTemplates"
                      item-text="name"
                      item-value="id"
                      @change="showPrintIcon = false"
                      :readonly="form.status && form.status.name_ && form.status.name_ !== 'printReady'"
                      :error-messages="errors.template_id"
                      :hint="
                        form.status.name_ && form.status.name_ !== 'printReady'
                          ? 'Nem módosítható'
                          : ''
                      "
                      persistent-hint
                      filled
                    />
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" lg="5">
                <v-card flat>
                  <v-card-title>Birtokos adatok</v-card-title>
                  <v-card-text>
                    <card-owner-form :card-owner="form.owner" :errors="errors.owner" readonly />
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" lg="2">
                <v-card flat>
                  <v-card-title>Neptun képzések</v-card-title>
                  <v-card-text>
                    <v-skeleton-loader
                      v-if="neptunCourses === null"
                      class="mx-auto"
                      type="list-item-three-line"
                    ></v-skeleton-loader>
                    <v-alert
                      v-if="neptunCourses && neptunCourses.length === 0"
                      icon="mdi-alert"
                      color="muted"
                    >
                      Nem található képzés a rendszerben.
                    </v-alert>

                    <v-list-item v-for="course in neptunCourses" :key="course.szakkod" class="px-0">
                      <v-list-item-icon class="mr-3">
                        <v-icon
                          v-if="course.statusz === 'Aktív'"
                          color="success"
                          :title="course.statusz"
                        >mdi-check-circle</v-icon
                        >
                        <v-icon v-else color="grey darken-1" :title="`Inaktív (${course.statusz})`">
                          mdi-close-circle-outline
                        </v-icon>
                      </v-list-item-icon>
                      <div>
                        <div>{{ course.szak }}</div>
                        <div>{{ course.tagozat }} ({{ course.szakkod }})</div>
                      </div>
                    </v-list-item>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card flat>
                  <v-card-title>Hozzáférési szintek</v-card-title>
                  <v-card-text>
                    <v-multi-select-list
                      :headers="[
                        { text: 'Megnevezés', value: 'name' },
                        { text: 'Lejárat', value: 'valid_to' },
                      ]"
                      :items="accessLevels"
                      v-model="form.access_levels"
                      item-value="element_id"
                      item-text="name"
                      unique-values
                      :blank-item-template="{ element_id: 0 }"
                    >
                      <template #[`item.name`]="{ item, items }">
                        <v-autocomplete
                          :items="items"
                          v-model="item.element_id"
                          item-text="name"
                          item-value="element_id"
                          placeholder="Válassz"
                          hide-details
                          filled
                          dense
                        />
                      </template>
                      <template #[`item.valid_to`]="{ item }">
                        <v-datetime-picker
                          v-model="item.valid_to"
                          :time-picker="false"
                          hide-details
                          dense
                          placeholder="Nincs lejárat"
                          filled
                        />
                      </template>
                    </v-multi-select-list>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <v-tab-item key="parking" :transition="false">
            <v-card flat>
              <v-card-title>Rendszámok</v-card-title>
              <v-card-text>
                <MultiSelectLists
                  :headers="[
                    { text: 'Név', value: 'name' },
                    { text: 'Rendszám', value: 'number' },
                  ]"
                  :items="form.plates"
                  v-model="form.plates"
                  hide-default-header
                  :blank-item-template="{ number: '', name: '' }"
                  key="plate"
                >
                  <template #[`item.name`]="{ item }">
                    <v-text-field
                      v-model="item.name"
                      filled
                      dense
                      :hide-details="!item.active_season_ticket"
                      :mask="$config.inputMasks.plateNumber"
                    />
                  </template>
                  <template #[`item.number`]="{ item, index }">
                    <v-masked-text-field
                      v-model="item.number"
                      :error-messages="errors[`plate-${index}`]"
                      filled
                      dense
                      :hide-details="!item.active_season_ticket"
                      v-uppercase
                      :mask="$config.inputMasks.plateNumber"
                    />
                  </template>
                </MultiSelectLists>
              </v-card-text>
            </v-card>

            <v-divider />

            <v-card v-if="hasFeature('PARKING_PENALTY')" flat>
              <v-card-title>Parkolási büntetések</v-card-title>
              <v-card-text>
                <parking-penalties />
              </v-card-text>
            </v-card>

            <v-divider />

            <v-card flat>
              <v-card-title>Parkolási figyelmeztetések</v-card-title>
              <v-card-text>
                <parking-warnings />
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item key="assets" :transition="false">
            <v-card flat>
              <v-card-title>
                <div class="d-flex align-center">
                  <span>Tulajdonodban</span>
                  <v-chip
                    v-if="assets && assets.length"
                    small
                    outlined
                    class="ml-2"
                    color="primary"
                  >
                    {{ assets.length }} db
                  </v-chip>
                </div>
              </v-card-title>

              <v-card-text>
                <v-row v-if="assets && assets.length">
                  <v-col
                    v-for="asset in assets"
                    :key="asset.id"
                    :cols="12"
                    :sm="6"
                    :md="4"
                    :lg="3">
                    <v-card rounded="lg" flat outlined>
                      <v-card-text>
                        <div class="d-flex align-center">
                          <v-sheet
                            :color="asset.color || 'grey'"
                            width="4"
                            height="32"
                            rounded
                            class="mr-3"
                          />
                          <div class="flex-grow-1">
                            <div class="font-weight-bold text-truncate">
                              {{ asset.name }}
                            </div>
                          </div>
                          <v-btn
                            v-if="hasRight('KEY_WRITE')"
                            icon
                            small
                            color="primary"
                            :to="{ name: 'AssetEditor', params: { id: asset.id } }"
                            title="Szerkesztés"
                          >
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <v-card v-else flat class="pa-6">
                  <div class="text--secondary">Jelenleg nincs kivett kulcs</div>
                </v-card>
              </v-card-text>
            </v-card>

            <v-card flat>
              <v-card-title>
                <div class="d-flex align-center">
                  <span>Összes kulcs</span>
                  <v-chip
                    v-if="dataTable.itemsLength"
                    color="primary"
                    small
                    outlined
                    class="ml-2">
                    {{ dataTable.itemsLength }} db
                  </v-chip>
                </div>
              </v-card-title>

              <v-card-text>
                <v-row class="mb-2" justify="start">
                  <v-col
                    :cols="12"
                    :sm="12"
                    :md="6"
                    :lg="3">
                    <v-text-field
                      v-model="assetSearch.name"
                      label="Keresés..."
                      dense
                      clearable
                      hide-details
                      filled
                      color="primary"
                      class="search-field"
                      @input="searchEventHandler"
                      v-mask="$config.inputMasks.name"
                    />
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="dataTable.headers"
                  :items="dataTable.items"
                  :server-items-length="dataTable.itemsLength"
                  :options.sync="dataTable.options"
                  :footer-props="$config.dataTableFooterProps"
                  @update:options="fetchItems"
                  elevation="2"
                  class="rounded-lg"
                >
                  <template #[`item.name`]="{ item }">
                    <div class="d-flex align-center">
                      <v-sheet
                        :color="`${item.color || 'grey'}`"
                        width="8"
                        height="24"
                        rounded="sm"
                        class="mr-2"
                      />
                      <span class="font-weight-bold">{{ item.name }}</span>
                    </div>
                  </template>

                  <template #[`item.cabinets`]="{ item }">
                    <v-chip
                      v-for="cabinet in item.cabinets"
                      :key="cabinet.id"
                      label
                      link
                      small
                      :to="{
                        name: 'AssetCabinetList',
                        params: { highlight: [cabinet.id] },
                      }"
                      class="ma-1"
                    >
                      {{ cabinet.name }}
                    </v-chip>
                  </template>

                  <template #[`item.actions`]="{ item }">
                    <v-btn
                      v-if="hasRight('KEY_WRITE')"
                      text
                      fab
                      small
                      dark
                      color="primary"
                      class="rounded-sm mr-1"
                      :to="{ name: 'AssetEditor', params: { id: item.id } }"
                      title="Szerkesztés"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item key="access_log" :transition="false">
            <v-card flat>
              <v-card-title>Belépés napló</v-card-title>
              <v-card-text>
                <access-log :card-id="form.element_id" />
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item key="system_log" :transition="false">
            <v-card flat>
              <v-card-title>Rendszer napló</v-card-title>
              <v-card-text>
                <admin-log
                  :card-id="form.element_id"
                  :access-levels="accessLevels"
                  :card-statuses="cardStatuses"
                />
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item key="access_check" :transition="false">
            <v-card flat>
              <v-card-title>Ellenőrzés</v-card-title>
              <v-card-text> </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </v-card>
  </div>
</template>

<script>
import CardOwnerForm from '@/components/forms/CardOwner.vue';
import { cardReader, dataTablePage } from '@/mixins';

export default {
  mixins: [cardReader, dataTablePage],

  components: {
    CardOwnerForm,
    AdminLog: () => import('@/pages/cards/AdminLog.vue'),
    AccessLog: () => import('@/pages/cards//AccessLog.vue'),
    ParkingPenalties: () => import('@/components/ParkingPenalties.vue'),
    ParkingWarnings: () => import('@/components/ParkingWarnings.vue'),
  },

  data() {
    return {
      tab: 'meta',
      form: {
        status: {},
        owner: {},
        access_levels: [],
      },
      formSnapshot: null,
      newAccessLevelId: 0,
      newAccessLevels: [],
      cardOwners: [],
      logs: [],
      accessLevels: [],
      cardStatuses: [],
      assets: [],
      cardAccessLevelsGroups: [],
      cardTemplates: [],
      showPrintIcon: true,
      parkingPenalty: {
        start_date: '',
        interval: '1 MONTH',
        reason: '',
      },
      errors: {},
      loading: false,
      socket: '',
      headers: [
        { text: 'Adminisztrátor', value: 'writer_name', sortable: false },
        { text: 'Módosítva', value: 'created', sortable: false },
        { value: 'v', sortable: false },
      ],
      select: null,
      search: {
        timeout: null,
      },
      neptunCourses: null,
      assetSearch: {
        name: '',
        taken: null,
        asset_cabinet_id: '',
        building_id: '',
        building_level_id: '',
      },
    };
  },

  beforeDestroy() {
    if (this.socket.readyState === WebSocket.OPEN) {
      this.socket.close();
    }
  },

  mounted() {
    this.fetchCard();
  },

  methods: {
    parseSearchQueries() {
      return {
        name: this.$route.query.name || '',
      };
    },

    setAccessLevelsFromGroup(group_id) {
      this.form.access_level_ids = [];
      this.cardAccessLevelsGroups.map((item) => {
        if (item.group_id == group_id) {
          this.form.access_level_ids = JSON.parse(JSON.stringify(item.access_level_ids));
        }
      });
    },

    async fetchCard() {
      try {
        this.loading = true;
        const response = await this.$http.get(`cards/details/${this.$route.params.id || 0}`);
        this.form = response.card;
        if (this.$route.params.ownerId) {
          this.form.owner_id = this.$route.params.ownerId;
        }
        this.formSnapshot = JSON.stringify(this.form);
        this.accessLevels = response.access_levels;
        this.cardStatuses = response.card_statuses;
        this.cardOwners = response.card_owners;
        this.assets = response.card.assets;
        this.cardAccessLevelsGroups = response.card_access_levels_groups;
        this.cardTemplates = response.card_templates;
        if (this.form.status === 'printReady') {
          this.showPrintIcon = true;
        }
        if (this.form.element_id) {
          if (this.form.status === 'idReading') this.watchCardReader();
        }
        if (this.form.element_id === 0 && !this.$route.params.id) {
          this.form.owner.element_id = this.$route.params.id;
        }
        this.formSnapshot = this.lodash.cloneDeep(this.form);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async save() {
      const data = this.lodash.cloneDeep(this.form);
      delete data.log;
      delete data.status;
      data.deleted_access_level_ids = this.lodash
        .differenceWith(
          this.formSnapshot.access_levels,
          this.form.access_levels,
          (a, b) => a.element_id === b.element_id
        )
        .map((e) => e.element_id);
      data.updated_access_levels = this.lodash.differenceWith(
        this.form.access_levels,
        this.formSnapshot.access_levels,
        (a, b) => a.element_id === b.element_id && a.valid_to === b.valid_to
      );
      data.deleted_plates = this.lodash.differenceWith(
        this.formSnapshot.plates,
        this.form.plates,
        (a, b) => a.id === b.id
      );

      this.loading = true;
      this.errors = {};
      try {
        const response = await this.$http.post('cards/save', data);
        if (this.lodash.size(response.errors) > 0) {
          this.errors = response.errors;
          return;
        }

        if (response.status === 'OK') {
          this.$dialog.notify.success('Sikeres mentés');
          if (this.$route.name === 'NewCard') {
            this.$router.push({ name: 'CardEditor', params: { id: response.card.element_id } });
          } else {
            this.fetchCard();
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async setRFID() {
      if (this.form.rf_id === '') {
        return;
      }
      const result = await this.$dialog.confirm({
        text: 'A megfelelő kártyát érintette az olvasóhoz?',
        title: 'Figyelem!',
        color: 'primary',
      });
      if (result) {
        this.loading = true;
        try {
          const data = {
            card_id: this.form.element_id,
            rf_id: this.form.rf_id,
          };
          await this.$http.post('card/set_rfid/', data);
          this.fetchCard();
        } catch (e) {
          console.error(e);
        } finally {
          this.loading = false;
        }
      } else {
        this.form.rf_id = '';
      }
    },

    async fetchItems() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.loading = true;
      try {
        const response = await this.$http.post('assets/list', this.postDatas, {
          signal: this.abortController.signal,
        });
        this.dataTable.items = response.assets;
        this.dataTable.itemsLength = response.assets_count;
        this.dataTable.headers = [
          { text: 'Név', value: 'name' },
          { text: 'Szekrények', value: 'cabinets', sortable: false },
          { text: 'Műveletek', value: 'actions', sortable: false, width: '150px' },
        ];
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error(e);
        }
      } finally {
        this.loading = false;
      }
    },
  },

  watch: {
    tab(newTab) {
      if (newTab === 2 && this.form.element_id) {
        console.log('fetching items');

        this.fetchItems();
      }
    }
  },

  computed: {
    assignedAccessLevels() {
      return this.lodash.filter(this.accessLevels, (al) => {
        console.log(this.form.access_level_ids);
        return this.lodash.includes(this.form.access_level_ids, al.element_id);
      });
    },

    searchQueries() {
      return {
        name: this.assetSearch.name || '',
      };
    },
    isDirty() {
      return JSON.stringify(this.form) === this.formSnapshot;
    },

    postDatas() {
      return {
        ...this.postData,
        card_id: this.form.element_id,
      };
    },
  },

  asyncComputed: {
    neptunCourses: {
      async get() {
        const response = await this.$http.get(`cards/neptun-courses/${this.$route.params.id || 0}`);
        return response.courses.sort((a, b) => a.tagozat < b.tagozat);
      },
      default: null,
    },
  },
};
</script>
